import { useQuery } from '@tanstack/react-query'
import { getTopSources, type SourceKind, type TopSource } from '@/api/topSources'
import type { TimeseriesPeriod } from '@/api/timeseries'

interface Props {
  channelUUID: string
  period: TimeseriesPeriod
}

const KIND_LABEL: Record<SourceKind, string> = {
  yandex_direct: 'Я.Директ',
  invite_link: 'Ссылка',
  unknown: 'Без источника',
}

function formatNum(n: number): string {
  return n.toLocaleString('ru-RU')
}

function formatRub(n: number): string {
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatDelta(pct: number): string {
  const sign = pct > 0 ? '+' : ''
  return `${sign}${pct.toFixed(1).replace('.', ',')} %`
}

function buildSparkPoints(values: number[]): string {
  if (values.length === 0) return ''
  const max = Math.max(...values, 1)
  const min = Math.min(...values, 0)
  const range = max - min || 1
  const w = 80
  const h = 24
  const stepX = w / Math.max(values.length - 1, 1)
  return values
    .map((v, i) => {
      const x = i * stepX
      const y = h - ((v - min) / range) * (h - 4) - 2
      return `${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')
}

function sparkColor(deltaPct: number | null): string {
  if (deltaPct === null) return '#8f9eb0'
  return deltaPct >= 0 ? '#00A860' : '#FF4000'
}

function Row({ row }: { row: TopSource }) {
  const cost = row.cost === null ? null : Math.round(row.cost)
  const cpf = row.cpf === null ? '—' : `${formatRub(row.cpf)} ₽`
  const delta =
    row.deltaPct === null
      ? '—'
      : formatDelta(row.deltaPct)
  const deltaCls =
    row.deltaPct === null ? 'delta-zero' : row.deltaPct >= 0 ? 'delta-up' : 'delta-dn'

  return (
    <tr>
      <td>
        <span className={`source-tag ${row.kind}`}>
          <span className="dot" /> {KIND_LABEL[row.kind]}
        </span>
      </td>
      <td>
        <div className="source-name">
          {row.name}
          {row.hint && (
            <>
              <br />
              <em>{row.hint}</em>
            </>
          )}
        </div>
      </td>
      <td className="num">{formatNum(row.subscribed)}</td>
      <td className="num">{cpf}</td>
      <td className={`num ${deltaCls}`}>{delta}</td>
      <td className="num">
        <svg className="spark" viewBox="0 0 80 24" preserveAspectRatio="none">
          <polyline
            points={buildSparkPoints(row.sparkline)}
            fill="none"
            stroke={sparkColor(row.deltaPct)}
            strokeWidth="1.5"
          />
        </svg>
      </td>
      <td className="num cost-col">{cost === null ? '—' : `${formatNum(cost)} ₽`}</td>
    </tr>
  )
}

export function TopSourcesTable({ channelUUID, period }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['topSources', channelUUID, period],
    queryFn: () => getTopSources(channelUUID, period, 5),
    enabled: !!channelUUID,
    staleTime: 60_000,
  })

  return (
    <div className="source-card">
      <div className="source-card-head">
        <div>
          <h3 className="card-title">
            Топ источников, <em>30 дней</em>
          </h3>
          <div className="card-sub">Откуда приходят подписчики и сколько они стоят</div>
        </div>
        {data && data.totalSources > data.sources.length && (
          <a className="card-action">
            Все источники
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M3 6h6M7 3l3 3-3 3" />
            </svg>
          </a>
        )}
      </div>

      {isLoading && <div className="src-empty">Загрузка…</div>}
      {data && data.sources.length === 0 && (
        <div className="src-empty">За 30 дней подписчиков пока нет</div>
      )}

      {data && data.sources.length > 0 && (
        <div className="src-table-wrap">
        <table className="src-table">
          <thead>
            <tr>
              <th>Источник</th>
              <th>Кампания / ссылка</th>
              <th className="num">Подписки</th>
              <th className="num">CPF</th>
              <th className="num">Δ vs пред.</th>
              <th className="num">Динамика</th>
              <th className="num cost-col">Расход</th>
            </tr>
          </thead>
          <tbody>
            {data.sources.map((row) => (
              <Row key={row.id} row={row} />
            ))}
          </tbody>
        </table>
        </div>
      )}

      <style>{`
        .source-card {
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 16px;
          padding: 22px 24px;
          margin-top: 16px;
        }
        .source-card-head {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }
        .card-title {
          margin: 0;
          font-size: 16px;
          font-weight: 700;
          color: var(--tg-ink, #111);
        }
        .card-title em {
          font-style: italic;
          font-family: 'Instrument Serif', serif;
          font-weight: 400;
          color: var(--tg-mute, #8f9eb0);
        }
        .card-sub {
          font-size: 12px;
          color: var(--tg-mute, #8f9eb0);
          margin-top: 4px;
        }
        .card-action {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 13px;
          font-weight: 600;
          color: var(--tg-blue-press, #0066cc);
          padding: 6px 10px;
          border-radius: 8px;
          cursor: pointer;
          transition: background 150ms;
        }
        .card-action:hover {
          background: rgba(0, 128, 255, 0.08);
        }
        .card-action svg {
          width: 12px;
          height: 12px;
        }
        .src-empty {
          padding: 24px;
          text-align: center;
          color: var(--tg-mute, #8f9eb0);
          font-size: 13px;
        }
        .src-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }
        .src-table th {
          text-align: left;
          padding: 10px 12px;
          font-weight: 600;
          font-size: 11px;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--tg-mute, #8f9eb0);
          border-bottom: 1px solid var(--tg-line-2, #f0f0f4);
        }
        .src-table th.num {
          text-align: right;
        }
        .src-table td {
          padding: 12px;
          border-bottom: 1px solid var(--tg-line-2, #f0f0f4);
          color: var(--tg-ink-2, #333);
          font-variant-numeric: tabular-nums;
        }
        .src-table tr:last-child td {
          border-bottom: 0;
        }
        .src-table tr:hover td {
          background: rgba(0, 128, 255, 0.04);
        }
        .src-table td.num {
          text-align: right;
          font-weight: 600;
        }
        .src-table td.delta-up { color: #00A860; }
        .src-table td.delta-dn { color: #FF4000; }
        .src-table td.delta-zero { color: var(--tg-mute, #8f9eb0); }
        .src-table .cost-col {
          color: var(--tg-mute, #8f9eb0);
          font-weight: 500;
        }

        .source-tag {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 600;
          background: var(--tg-surface-2, #f5f5f9);
          color: var(--tg-ink-2, #333);
        }
        .source-tag .dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
        }
        .source-tag.yandex_direct .dot { background: rgb(255, 89, 0); }
        .source-tag.invite_link .dot { background: var(--tg-blue, #0080FF); }
        .source-tag.unknown .dot { background: #8f9eb0; }

        .src-table-wrap {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        .source-name {
          font-weight: 600;
          color: var(--tg-ink, #111);
          margin-bottom: 2px;
          word-break: break-word;
          max-width: 280px;
        }
        @media (max-width: 720px) {
          .src-table { min-width: 540px; }
          .source-name { max-width: 200px; font-size: 12px; }
          .source-name em { display: block; }
        }
        .source-name em {
          font-family: 'Instrument Serif', serif;
          font-style: italic;
          font-weight: 400;
          color: var(--tg-mute, #8f9eb0);
        }
        .spark {
          width: 80px;
          height: 24px;
          display: block;
          margin-left: auto;
        }
      `}</style>
    </div>
  )
}
